import { supabase } from '../lib/supabase';

// jyyb8AfJ PR 2 — thin wrapper around the set-user-deactivation edge fn.
//
// Uses raw fetch (not supabase.functions.invoke) so the caller can see the
// JSON body of non-2xx responses — the edge fn returns a structured
// { error, failingStep } payload that the UI surfaces verbatim. Same
// rationale as stripeService.createCheckoutSession.
//
// Non-optimistic: caller waits for the edge fn to confirm both the
// user_profiles state change AND the auth.users ban toggle before
// updating local UI state.

async function callSetUserDeactivation(userId, active) {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    const token = session?.access_token;
    if (!token) {
      return {
        success: false,
        error: 'Not authenticated',
        failingStep: 'client_no_session',
      };
    }

    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/set-user-deactivation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ userId, active }),
      }
    );

    let json;
    try { json = await response?.json(); } catch (_) { json = {}; }

    if (!response?.ok || json?.success !== true) {
      return {
        success: false,
        error: json?.error || `Request failed (HTTP ${response?.status})`,
        failingStep: json?.failingStep || `http_${response?.status}`,
        status: response?.status,
      };
    }

    return { success: true, userId: json?.userId, active: json?.active };
  } catch (err) {
    return {
      success: false,
      error: err?.message || 'Network error',
      failingStep: 'client_exception',
    };
  }
}

export const userService = {
  /**
   * Deactivate a user. Admin-only (enforced server-side).
   * On success the user_profiles row carries deactivated_at + deactivated_by,
   * and auth.users.banned_until is set ~100 years out so refresh tokens fail.
   * @param {string} userId
   * @returns {Promise<{ success: boolean, error?: string, failingStep?: string, status?: number }>}
   */
  deactivateUser(userId) {
    return callSetUserDeactivation(userId, false);
  },

  /**
   * Reactivate a user. Admin-only (enforced server-side).
   * Clears deactivated_at, deactivated_by, and lifts the auth ban.
   * @param {string} userId
   */
  reactivateUser(userId) {
    return callSetUserDeactivation(userId, true);
  },
};
